import React, { useEffect } from 'react';
import { Cascader, Col, Form, FormInstance } from 'antd';
import { OrganizationUnitShiftConfigFlatType } from '@scheduling/graphql';
import { momentForDomain } from '@common/services/moment-shadow';
import { formatNumberDigits } from '@common/services';

const moment = momentForDomain('scheduling', 'shifts');

export function ShiftDurationInput({
  form,
  startTime,
  shiftConfigs,
  disabled = false,
  loading = false,
}: {
  form: FormInstance;
  shiftConfigs: OrganizationUnitShiftConfigFlatType[];
  startTime?: string;
  disabled?: boolean;
  loading?: boolean;
}) {
  const optionsFromShiftConfigs = (shiftConfigsOptions: number[]) => {
    return shiftConfigsOptions.map((duration) => {
      const hours = Math.floor(duration);
      const minutes = (duration - hours) * 60;

      return {
        label: `${hours} hrs ${minutes} min`,
        value: duration.toFixed(2),
      };
    });
  };

  const minutesOptions = [
    { label: '0 min', floating: 0.0 },
    { label: '15 min', floating: 0.25 },
    { label: '30 min', floating: 0.5 },
    { label: '45 min', floating: 0.75 },
  ];

  const optionsFromAnyDuration = Array.from({ length: 24 }, (_, i) => {
    const duration = i + 1;

    return {
      label: `${duration} hrs`,
      value: duration.toString(),
      children: minutesOptions.map((minute) => ({
        disabled: duration === 24 && minute.floating !== 0.0,
        value: (duration + minute.floating).toFixed(2),
        ...minute,
      })),
    };
  });

  const availableHours = [
    ...new Set(
      shiftConfigs
        .filter(
          (shiftConfig) =>
            moment(shiftConfig.startTime).format('HH:mm') === startTime
        )
        .map((shiftConfig) => shiftConfig.duration)
        .sort((a, b) => a - b)
    ),
  ];

  const limitedByShiftConfigs = availableHours.length > 0;

  const options = limitedByShiftConfigs
    ? optionsFromShiftConfigs(availableHours)
    : optionsFromAnyDuration;

  useEffect(() => {
    const currentDuration = form.getFieldValue('shiftDuration');

    if (limitedByShiftConfigs && startTime) {
      if (
        !currentDuration ||
        !options.find((opt) => opt.value === currentDuration?.[0])
      ) {
        form.setFieldsValue({
          shiftDuration: [options[options.length - 1]?.value],
        });
      }
    } else if (!limitedByShiftConfigs && !currentDuration) {
      form.setFieldsValue({
        shiftDuration: ['12', '12.00'],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableHours, startTime, options, form]);

  return (
    <>
      <Col span={12}>
        <Form.Item
          label="Duration"
          name="shiftDuration"
          className="wk-shift-duration-field"
          rules={[{ required: true, message: 'Please enter a shift duration' }]}
        >
          <Cascader
            disabled={
              disabled || loading || (limitedByShiftConfigs && !startTime)
            }
            size="large"
            onChange={(value) => {
              form.setFieldsValue({ shiftDuration: value });
            }}
            placeholder="Select duration"
            getPopupContainer={(trigger) => trigger.parentNode}
            options={options}
            displayRender={(_, options) => {
              const selectedOptions = options?.filter((x) => x) || [];
              if (selectedOptions.length === 0) {
                return '';
              }

              const duration = Number(
                selectedOptions.filter((x) => x).length > 1
                  ? options?.[1].value
                  : options?.[0].value
              );

              return `${formatNumberDigits(duration)} hrs`;
            }}
            defaultValue={['12', '12.00']}
            expandIcon={<></>}
          />
        </Form.Item>
      </Col>
    </>
  );
}
